import Loadable from 'react-loadable';

import PropTypes from 'prop-types';

import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { Box, Button, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';

import VideoDownloadContainer from './VideoDownloadContainer';

import VideoTimelineComponent from './VideoTimelineComponent';

import { isAdmin, isPM } from '../../utils';
import { getIsSixteenByNineAspectRatio } from '../Camera/utils';
import { formatVideoTimestamp } from './utils';

import { TIMELINE_DURATION_OPTIONS } from './constants';

import LeftArrowIcon from '../../res/svg/LeftArrowIcon';
import RightArrowIcon from '../../res/svg/RightArrowIcon';
import { LOADABLE_DELAY, LOADABLE_TIME_OUT } from '../../staticData';
import {
  StyledCameraContent,
  StyledSixteenByNineAspectRatio,
} from '../Camera/styledComponents';
import LoadingPlaceholder from '../LoadingPlaceholder';
import StyledSelect from '../Select';
import DateTimeFnsPicker from './DateTimeFnsPicker';

const EagleEyeVideoEmbedComponent = Loadable({
  loader: () => import('./EagleEyeVideoEmbedComponent'),
  loading: LoadingPlaceholder,
  delay: LOADABLE_DELAY,
  timeout: LOADABLE_TIME_OUT,
});

const ScrollButton = styled(Button)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  minWidth: '2.6rem',
  minHeight: '2.6rem',
  height: '2.6rem',
  width: '2.6rem',
  backgroundColor: theme.palette.common.white,
  border: `.1rem solid #cfcfcf`,
  borderRadius: `.5rem 0 0 .5rem`,
  top: '1.5rem',
  '&.scroll-next': {
    position: 'relative',
    zIndex: 11,
    borderRadius: `0 .5rem .5rem 0`,
  },
  '&.scroll-prev': {
    position: 'relative',
    zIndex: 11,
  },
  '& .MuiButton-text': {
    padding: 2,
  },
  '& svg': {
    width: 15,
    height: 15,
    fill: theme.palette.custom.primary2,
  },
}));

const VideoControlIcon = styled(IconButton)(() => ({
  position: 'absolute',
  top: 0,
  fontSize: 'inherit',
}));

const VideoControlIconComponent = ({
  isPlayOn,
  onPauseVideo,
  selectedCamera,
  isStreamOn,
  onPlayVideo,
}) => {
  if (isPlayOn) {
    return (
      <VideoControlIcon onClick={onPauseVideo} className="pause-button">
        <PauseIcon fontSize="inherit" style={{ color: 'white' }} />
      </VideoControlIcon>
    );
  }
  if (selectedCamera && (!isStreamOn || !isPlayOn)) {
    return (
      <VideoControlIcon onClick={onPlayVideo}>
        <PlayArrowIcon fontSize="inherit" style={{ color: 'white' }} />
      </VideoControlIcon>
    );
  }
  return <VideocamOutlinedIcon fontSize="inherit" />;
};
VideoControlIconComponent.defaultProps = {
  selectedCamera: null,
};
VideoControlIconComponent.propTypes = {
  isPlayOn: PropTypes.bool.isRequired,
  onPauseVideo: PropTypes.func.isRequired,
  selectedCamera: PropTypes.instanceOf(Object),
  isStreamOn: PropTypes.bool.isRequired,
  onPlayVideo: PropTypes.func.isRequired,
};

const EEHistoryPlaybackComponent = (props) => {
  const {
    building,
    camera,

    timestamp,
    onTimestampChange,

    startTime,
    endTime,
    isStreamOn,
    isPlayOn,
    onPlayVideo,
    onPauseVideo,

    timeline,
    setTimeline,
    timelineMarks,
    onTimeUpdateCallback,
    onVideoEnded,
    setVideoConfig,
    videoConfig,
    onScrollTimelineBack,
    onScrollTimelineForth,

    timelineDuration,
    setTimelineDuration,

    downloadData,
    setDownloadData,

    isNextTimeDisabled,
  } = props;

  return (
    <>
      <Box my="2rem">
        <Box style={{ position: 'relative' }}>
          <StyledSixteenByNineAspectRatio />
          <StyledCameraContent>
            <Box
              className="center-content"
              fontSize="20rem"
              width="20rem"
              height="20rem"
              zIndex={1}
            >
              <VideoControlIconComponent
                isPlayOn={isPlayOn}
                onPauseVideo={onPauseVideo}
                selectedCamera={camera}
                isStreamOn={isStreamOn}
                onPlayVideo={onPlayVideo}
              />
            </Box>
            {camera?.id && startTime && isStreamOn ? (
              <EagleEyeVideoEmbedComponent
                cameraId={camera.liveViewId}
                buildingName={building?.name}
                cameraName={camera.name}
                buildingId={building?.id}
                startTime={startTime}
                endTime={endTime}
                isLiveVideo={false}
                showControls={false}
                onTimeUpdateCallback={onTimeUpdateCallback}
                currentVideoTimeFormatted={formatVideoTimestamp(timeline)}
                setVideoConfig={setVideoConfig}
                onVideoEnded={onVideoEnded}
                timestamp={startTime}
                isSixteenByNineAspectRatio={getIsSixteenByNineAspectRatio(
                  camera,
                )}
                isHistoryPlayback
              />
            ) : null}
          </StyledCameraContent>
        </Box>
      </Box>

      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="space-between"
        alignItems="center"
        gap="1rem"
      >
        <Box display="flex" flexGrow={2}>
          <DateTimeFnsPicker value={timestamp} onChange={onTimestampChange} />
        </Box>
        <StyledSelect
          value={timelineDuration}
          onChange={setTimelineDuration}
          options={TIMELINE_DURATION_OPTIONS}
          placeholder="Choose duration"
          sortAlphabetically={false}
          disableClearable
        />
        {downloadData?.isShown && (
          <VideoDownloadContainer downloadData={downloadData} />
        )}
        {Boolean(videoConfig && timeline) && (isAdmin() || isPM()) && (
          <Button
            color="primary"
            variant="outlined"
            onClick={() =>
              setDownloadData({
                isShown: true,
                config: videoConfig,
                currentVideoTime: timeline,
                setDownloadData,
                buildingName: building?.name,
                cameraName: camera?.name,
              })
            }
            sx={{ flexGrow: 1 }}
          >
            Download
          </Button>
        )}
      </Box>

      <Box display="flex" position="relative" mt="1.5rem">
        <ScrollButton
          className="scroll-prev"
          onClick={onScrollTimelineBack}
          aria-label="Prev hours"
        >
          <LeftArrowIcon />
        </ScrollButton>

        <VideoTimelineComponent
          value={timeline}
          setValue={setTimeline}
          marks={timelineMarks}
          isNextTimeDisabled={isNextTimeDisabled}
        />

        <ScrollButton
          className="scroll-next"
          onClick={onScrollTimelineForth}
          aria-label="Next hours"
          disabled={isNextTimeDisabled}
        >
          <RightArrowIcon />
        </ScrollButton>
      </Box>
    </>
  );
};

EEHistoryPlaybackComponent.defaultProps = {
  timelineMarks: null,
  videoConfig: null,
  startTime: null,
  endTime: null,
};

EEHistoryPlaybackComponent.propTypes = {
  building: PropTypes.instanceOf(Object).isRequired,
  camera: PropTypes.instanceOf(Object).isRequired,

  startTime: PropTypes.string,
  endTime: PropTypes.string,
  isStreamOn: PropTypes.bool.isRequired,
  isPlayOn: PropTypes.bool.isRequired,
  onPlayVideo: PropTypes.func.isRequired,
  onPauseVideo: PropTypes.func.isRequired,

  timestamp: PropTypes.instanceOf(Object).isRequired,
  onTimestampChange: PropTypes.func.isRequired,

  timeline: PropTypes.number.isRequired,
  setTimeline: PropTypes.func.isRequired,
  timelineMarks: PropTypes.instanceOf(Array),
  onTimeUpdateCallback: PropTypes.func.isRequired,
  onVideoEnded: PropTypes.func.isRequired,
  setVideoConfig: PropTypes.func.isRequired,
  videoConfig: PropTypes.instanceOf(Object),
  onScrollTimelineBack: PropTypes.func.isRequired,
  onScrollTimelineForth: PropTypes.func.isRequired,

  timelineDuration: PropTypes.instanceOf(Object).isRequired,
  setTimelineDuration: PropTypes.func.isRequired,

  downloadData: PropTypes.instanceOf(Object).isRequired,
  setDownloadData: PropTypes.func.isRequired,

  isNextTimeDisabled: PropTypes.bool.isRequired,
};

export default EEHistoryPlaybackComponent;
